import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { ModernButton } from '../ui/ModernButton';
import { FlowingLines } from '../ui/FlowingLines';
import { dbService, userDisplayName } from '../../services/DatabaseService';
import imageUploadService from '../../services/ImageUploadService';
import { compressImageFile, dataUrlToBytes, MAX_IMAGE_BYTES, MAX_GIF_BYTES } from '../../utils/helpers';
import { createQuestionPage, normalizeQuestionnaireData } from '../../utils/questionnairePages';
import { moveQuestionInList, reorderQuestionsByDrag } from '../../utils/questionOrdering';
import { parseQuestionnaireImport, preserveQuestionIds, AI_IMPORT_PROMPT, QUESTIONNAIRE_IMPORT_SCHEMA } from '../../utils/questionnaireImport';
import {
  INTRO_PAGE_ID,
  DEFAULT_QUESTIONS_PAGE_ID,
  PAGE_TYPE_BASIS,
  PAGE_TYPE_FACH,
  DEFAULT_CUSTOM_PAGE_PRESETS,
  NAME_QUESTION_SEED
} from '../../utils/questionnaireEditorConstants';
import {
  normalizeCustomPageType,
  inferCustomPageTypeByIndex,
  clampDefaultQuestionsPageOrder,
  createDefaultCustomPages,
  buildSectionsLine,
  resolvePageTitle,
  extractLegacySectionConfig,
  dedupeOptionScores
} from '../../utils/questionnaireEditorUtils';
import { renderPointScoringPrompt, describeGeminiApiError } from '../../utils/aiPrompts';
import { geminiFetch } from '../../services/geminiConfig';
import { EvaluationProgress, markStepStart, markStepEnd } from '../ui/EvaluationProgress';
import {
  EditorActions, AISection, TemplateLoadingSection, RichTextSections,
  PagesPanel, IntroPageContent, DefaultQuestionsContent, CustomPageContent,
  RailResizer
} from '../questionnaire-editor';
import { TemplateConfirmModal } from '../modals/TemplateConfirmModal';
import { DefaultTemplateConfirmModal } from '../modals/DefaultTemplateConfirmModal';
import { LinkModal } from '../modals/LinkModal';
import { useRichTextFields } from '../../hooks/useRichTextFields';
import { useQuestionsCRUD } from '../../hooks/useQuestionsCRUD';
import { useEditorDragDrop } from '../../hooks/useEditorDragDrop';
import { useEditorAI } from '../../hooks/useEditorAI';
import { useAutoSave } from '../../hooks/useAutoSave';
import { useEditorTemplates } from '../../hooks/useEditorTemplates';
import { useResizableRail } from '../../hooks/useResizableRail';

// --- Page Navigation FAB ---
function PageNavigationFab({ selectedPageId, setSelectedPageId, orderedEditorQuestionPages, isDefaultQuestionsPageSelected, defaultSectionTitle }) {
  const [isOpen, setIsOpen] = useState(false);
  const fabRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (fabRef.current && !fabRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const pages = [
    { id: INTRO_PAGE_ID, label: 'Einführung', order: 1 },
    ...orderedEditorQuestionPages.map((item) => ({
      id: item.type === 'default' ? DEFAULT_QUESTIONS_PAGE_ID : item.page?.id,
      label: item.type === 'default'
        ? (defaultSectionTitle || 'Formale Fragen')
        : (item.page?.title || `Seite ${item.overallOrder}`),
      order: item.overallOrder
    }))
  ];

  const currentPage = pages.find((p) => p.id === selectedPageId) || pages[0];

  return (
    <div ref={fabRef} className="fixed bottom-6 right-6 z-50 flex flex-col-reverse items-end gap-2 lg:hidden">
      {/* Main FAB button */}
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        className={`w-14 h-14 rounded-full shadow-lg flex items-center justify-center text-white transition-all duration-200 ${
          isOpen
            ? 'bg-slate-600 hover:bg-slate-700 rotate-45'
            : 'bg-green-600 hover:bg-green-700'
        }`}
        title="Seiten-Navigation"
      >
        {isOpen ? (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v12m6-6H6" />
          </svg>
        ) : (
          <span className="text-sm font-bold">{currentPage?.order || 1}</span>
        )}
      </button>

      {/* Expanded page buttons */}
      {isOpen && pages.map((page, index) => {
        const isSelected = page.id === selectedPageId;
        return (
          <div
            key={page.id}
            className="flex items-center gap-2 animate-fadeIn"
            style={{ animationDelay: `${index * 30}ms` }}
          >
            <span className="px-2 py-1 text-xs font-medium bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-lg shadow-md border border-slate-200 dark:border-slate-700 max-w-[140px] truncate">
              {page.label}
            </span>
            <button
              onClick={() => { setSelectedPageId(page.id); setIsOpen(false); }}
              className={`w-10 h-10 rounded-full shadow-md flex items-center justify-center text-sm font-bold transition-all duration-150 ${
                isSelected
                  ? 'bg-green-600 text-white ring-2 ring-green-300 dark:ring-green-800'
                  : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 hover:bg-green-50 dark:hover:bg-slate-700'
              }`}
              title={page.label}
            >
              {page.order}
            </button>
          </div>
        );
      })}
    </div>
  );
}

// --- Der Fragebogen-Editor mit Gemini API Integration ---
export function QuestionnaireEditorPage({ navigateTo }) {
  const [title, setTitle] = useState('');
  const [questions, setQuestions] = useState([]);
  const [questionPages, setQuestionPages] = useState(() => createDefaultCustomPages());
  const [selectedPageId, setSelectedPageId] = useState(null);
  const [introText, setIntroText] = useState('Bitte füllen Sie die folgenden Felder aus.');
  const [outroText, setOutroText] = useState('Vielen Dank für Ihre Zeit und die sorgfältigen Antworten. Wir haben Ihre Unterlagen erhalten und werden sie nun intern prüfen.\n\nWir bemühen uns, Ihnen so schnell wie möglich eine Rückmeldung zu geben und werden Sie, wie vereinbart, in Kürze kontaktieren, um Sie über den weiteren Verlauf des Bewerbungsprozesses zu informieren.');
  const [defaultIntroText] = useState('Bitte füllen Sie die folgenden Felder aus.');
  const [defaultOutroText] = useState('Vielen Dank für Ihre Zeit und die sorgfältigen Antworten. Wir haben Ihre Unterlagen erhalten und werden sie nun intern prüfen.\n\nWir bemühen uns, Ihnen so schnell wie möglich eine Rückmeldung zu geben und werden Sie, wie vereinbart, in Kürze kontaktieren, um Sie über den weiteren Verlauf des Bewerbungsprozesses zu informieren.');
  const [showIntroField, setShowIntroField] = useState(false);
  const [showOutroField, setShowOutroField] = useState(false);
  const [showCancelField, setShowCancelField] = useState(false);
  const [showIntroPageFields, setShowIntroPageFields] = useState(false);
  const [introWelcomeText, setIntroWelcomeText] = useState('');
  const [introOverviewText, setIntroOverviewText] = useState('');
  const [introAfterSectionsText, setIntroAfterSectionsText] = useState('');
  const [defaultSectionTitle, setDefaultSectionTitle] = useState('Formale Fragen');
  const [defaultSectionDescription, setDefaultSectionDescription] = useState('');
  const [reviewSectionTitle, setReviewSectionTitle] = useState('Überprüfung');
  const [reviewSectionDescription, setReviewSectionDescription] = useState('');
  const [, setIntroSectionsText] = useState('');
  const [introTimerText, setIntroTimerText] = useState('');
  const [introStartButtonText, setIntroStartButtonText] = useState('');
  const [cancelText, setCancelText] = useState('Du hast gemerkt, dass wir vielleicht nicht die richtige Passung sind? Kein Problem — das ist für beide Seiten wichtig! Hier kannst du uns einfach mitteilen, dass du deine Bewerbung zurückziehen möchtest.');
  const [cancelOutroText, setCancelOutroText] = useState('Vielen Dank für deine Zeit! Du hast deine Bewerbung erfolgreich abgesagt. Falls du dich doch nochmal entscheidest, kannst du gerne eine neue Bewerbung einreichen.');
  const [autoSubmitEnabled, setAutoSubmitEnabled] = useState(false);
  const [allowMultipleSubmissions, setAllowMultipleSubmissions] = useState(false);
  // Internal accounts for the "Zuständig" picker. The selected owner is
  // pre-filled from the loaded template (not auto-set to the current user).
  const [accounts, setAccounts] = useState([]);
  const [assignedTo, setAssignedTo] = useState('');
  const [showDefaultQuestionsFields, setShowDefaultQuestionsFields] = useState(false);
  const [defaultQuestionsPageOrder, setDefaultQuestionsPageOrder] = useState(() => clampDefaultQuestionsPageOrder(DEFAULT_CUSTOM_PAGE_PRESETS.length + 1, DEFAULT_CUSTOM_PAGE_PRESETS.length));
  const [defaultQuestions, setDefaultQuestions] = useState([NAME_QUESTION_SEED]);
  const [aiJobTitle, setAiJobTitle] = useState('');
  const [aiShouldFillDescriptions, setAiShouldFillDescriptions] = useState(false);
  const [error, setErrorState] = useState(null);
  const [generatedLink, setGeneratedLink] = useState(null);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showMultipleSubmissionWarningModal, setShowMultipleSubmissionWarningModal] = useState(false);
  // Import (AI-generated / exported) questionnaire JSON
  const [showImportModal, setShowImportModal] = useState(false);
  const [importText, setImportText] = useState('');
  const [importError, setImportError] = useState('');
  const hasLoadedDraftRef = useRef(false);

  // Scoring config state
  const [showScoringConfig, setShowScoringConfig] = useState(false);
  const [scoringTotalScore, setScoringTotalScore] = useState(100);
  const [scoringPrompt, setScoringPrompt] = useState('');
  const [pageMaxScores, setPageMaxScores] = useState({});
  const [isSettingUpScoring, setIsSettingUpScoring] = useState(false);
  const [scoringProgress, setScoringProgress] = useState(null);

  const isOptionScoringQuestion = useCallback((question) => {
    if (!question || !Array.isArray(question.options) || question.options.length === 0) return false;
    if (question.type === 'single-choice' || question.type === 'multiple-choice' || question.type === 'matrix' || question.type === 'combobox' || question.type === 'ranking') return true;
    if (question.type === 'image' && (question.answerType === 'single-choice' || question.answerType === 'multiple-choice')) return true;
    return false;
  }, []);

  const normalizeScoresToTotal = useCallback((scores = [], targetTotal = 0) => {
    const sanitizedScores = (scores || []).map((value) => {
      const numericValue = Number(value);
      if (!Number.isFinite(numericValue) || numericValue < 0) return 0;
      return Math.round(numericValue);
    });

    if (!sanitizedScores.length) return [];

    const normalizedTarget = Math.max(1, Math.round(Number(targetTotal) || 0));
    const currentTotal = sanitizedScores.reduce((sum, score) => sum + score, 0);
    if (currentTotal === normalizedTarget) return sanitizedScores;

    if (currentTotal <= 0) {
      const base = Math.floor(normalizedTarget / sanitizedScores.length);
      let remainder = normalizedTarget - base * sanitizedScores.length;
      return sanitizedScores.map(() => {
        if (remainder > 0) {
          remainder -= 1;
          return base + 1;
        }
        return base;
      });
    }

    const scaledScores = sanitizedScores.map((score) => (score / currentTotal) * normalizedTarget);
    const roundedDownScores = scaledScores.map((value) => Math.floor(value));
    let remainder = normalizedTarget - roundedDownScores.reduce((sum, score) => sum + score, 0);

    if (remainder > 0) {
      const order = scaledScores
        .map((value, index) => ({ index, fraction: value - Math.floor(value) }))
        .sort((a, b) => b.fraction - a.fraction);
      for (let i = 0; i < remainder; i += 1) {
        roundedDownScores[order[i % order.length].index] += 1;
      }
    } else if (remainder < 0) {
      const order = roundedDownScores
        .map((value, index) => ({ index, value }))
        .sort((a, b) => b.value - a.value);
      let deficit = Math.abs(remainder);
      let cursor = 0;
      while (deficit > 0 && order.length > 0) {
        const current = order[cursor % order.length];
        if (roundedDownScores[current.index] > 0) {
          roundedDownScores[current.index] -= 1;
          deficit -= 1;
        }
        cursor += 1;
      }
    }

    return roundedDownScores;
  }, []);

  // Function to show notifications
  const showNotification = (message, type = 'success') => {
    if (!message) return;
    if (type === 'error') {
      toast.error(message, { duration: 4500 });
      return;
    }
    toast.success(message, { duration: 3000 });
  };

  const setError = useCallback((message) => {
    setErrorState(message);
    if (!message) return;
    toast.error(String(message), { duration: 4500 });
  }, []);

  // --- Rich text fields hook ---
  const {
    introTextRef, outroTextRef, cancelTextRef, cancelOutroTextRef,
    isTypingIntro, isTypingOutro, isTypingCancel, isTypingCancelOutro,
    introTypingTimeout, outroTypingTimeout, cancelTypingTimeout, cancelOutroTypingTimeout,
    debouncedIntroUpdate, debouncedOutroUpdate, debouncedCancelUpdate, debouncedCancelOutroUpdate,
    applyIntroFormat, applyOutroFormat, applyCancelFormat, applyCancelOutroFormat
  } = useRichTextFields({
    introText, setIntroText,
    outroText, setOutroText,
    cancelText, setCancelText,
    cancelOutroText, setCancelOutroText,
    showIntroField, showOutroField, showCancelField
  });

  const sectionsTextForSave = useMemo(() => {
    const defaultLine = buildSectionsLine(defaultSectionTitle, defaultSectionDescription);
    const reviewLine = buildSectionsLine(reviewSectionTitle, reviewSectionDescription);
    const customPageLines = (questionPages || []).map((page, index) =>
      buildSectionsLine(String(page?.title || `Seite ${index + 1}`).trim(), String(page?.description || '').trim())
    );
    const orderedQuestionLines = [];
    const totalQuestionPages = customPageLines.length + 1;
    const defaultOrder = clampDefaultQuestionsPageOrder(defaultQuestionsPageOrder, customPageLines.length);
    let customCursor = 0;

    for (let position = 1; position <= totalQuestionPages; position += 1) {
      if (position === defaultOrder) {
        if (defaultLine) orderedQuestionLines.push(defaultLine);
        continue;
      }

      if (customPageLines[customCursor]) {
        orderedQuestionLines.push(customPageLines[customCursor]);
      }
      customCursor += 1;
    }

    return [
      ...orderedQuestionLines,
      reviewLine
    ].filter(Boolean).join('\n');
  }, [questionPages, defaultQuestionsPageOrder, defaultSectionTitle, defaultSectionDescription, reviewSectionTitle, reviewSectionDescription]);

  const buildEditorStructure = useCallback((sourceQuestions = [], sourcePages = questionPages) => {
    const pages = Array.isArray(sourcePages) ? sourcePages : [];
    const initialPageMap = new Map(
      pages.map((page, index) => [
        page.id,
        {
          id: page.id,
          title: resolvePageTitle(page.title, `Seite ${index + 1}`),
          description: page.description || '',
          order: index + 1,
          pageType: normalizeCustomPageType(page.pageType, inferCustomPageTypeByIndex(index)),
          questions: []
        }
      ])
    );

    (sourceQuestions || []).forEach((question) => {
      if (!question?.pageId || initialPageMap.has(question.pageId)) return;
      initialPageMap.set(question.pageId, {
        id: question.pageId,
        title: resolvePageTitle(question.pageTitle, `Seite ${initialPageMap.size + 1}`),
        description: question.pageDescription || '',
        order: Number.isFinite(Number(question.pageOrder)) ? Number(question.pageOrder) : (initialPageMap.size + 1),
        pageType: normalizeCustomPageType(question.pageType, inferCustomPageTypeByIndex(initialPageMap.size)),
        questions: []
      });
    });

    if (initialPageMap.size === 0) {
      createDefaultCustomPages().forEach((page) => {
        initialPageMap.set(page.id, { ...page, questions: [] });
      });
    }

    const orderedSeedPages = Array.from(initialPageMap.values()).sort((a, b) => (a.order || 0) - (b.order || 0));
    const pageMap = new Map(
      orderedSeedPages.map((page, index) => [
        page.id,
        {
          id: page.id,
          title: resolvePageTitle(page.title, `Seite ${index + 1}`),
          description: page.description || '',
          order: index + 1,
          pageType: normalizeCustomPageType(page.pageType, inferCustomPageTypeByIndex(index)),
          questions: []
        }
      ])
    );

    const firstPageId = orderedSeedPages[0]?.id || Array.from(pageMap.keys())[0];
    const normalizedQuestions = (sourceQuestions || []).map((question) => {
      const pageId = question.pageId && pageMap.has(question.pageId) ? question.pageId : firstPageId;
      const normalizedQuestion = { ...question, pageId };
      pageMap.get(pageId).questions.push(normalizedQuestion);
      return normalizedQuestion;
    });

    const normalized = normalizeQuestionnaireData({
      questions: normalizedQuestions,
      questionPages: Array.from(pageMap.values())
    });

    return {
      questions: normalized.questions || [],
      questionPages: (normalized.questionPages || []).map((page, index) => ({
        id: page.id,
        title: resolvePageTitle(page.title, `Seite ${index + 1}`),
        description: page.description || '',
        order: index + 1,
        pageType: normalizeCustomPageType(page.pageType, inferCustomPageTypeByIndex(index))
      }))
    };
  }, [questionPages]);

  const applyEditorStructure = useCallback((sourceQuestions = [], sourcePages = questionPages) => {
    const normalized = buildEditorStructure(sourceQuestions, sourcePages);
    setQuestions(normalized.questions);
    setQuestionPages(normalized.questionPages);
    setSelectedPageId((prev) => (
      prev === INTRO_PAGE_ID || prev === DEFAULT_QUESTIONS_PAGE_ID
        ? prev
        : (
      normalized.questionPages.some((page) => page.id === prev)
        ? prev
        : (normalized.questionPages[0]?.id || null)
        )
    ));
    return normalized;
  }, [buildEditorStructure, questionPages]);

  // --- Page management ---
  const addQuestionPage = () => {
    const hasBasisPage = questionPages.some((page) => normalizeCustomPageType(page.pageType, PAGE_TYPE_FACH) === PAGE_TYPE_BASIS);
    const newPageType = hasBasisPage ? PAGE_TYPE_FACH : PAGE_TYPE_BASIS;
    const newPage = createQuestionPage({ title: `Seite ${questionPages.length + 1}`, pageType: newPageType });
    const nextPages = [...questionPages, { id: newPage.id, title: newPage.title, description: '', order: questionPages.length + 1, pageType: newPageType }];
    applyEditorStructure(questions, nextPages);
    setSelectedPageId(newPage.id);
  };

  const renameQuestionPage = (pageId, titleValue) => {
    const nextPages = questionPages.map((page) => (
      page.id === pageId ? { ...page, title: titleValue } : page
    ));
    applyEditorStructure(questions, nextPages);
  };

  const updateQuestionPageDescription = (pageId, descriptionValue) => {
    const nextPages = questionPages.map((page) => (
      page.id === pageId ? { ...page, description: descriptionValue } : page
    ));
    applyEditorStructure(questions, nextPages);
  };

  const updateQuestionPageType = (pageId, pageType) => {
    const nextPages = questionPages.map((page) => (
      page.id === pageId ? { ...page, pageType: normalizeCustomPageType(pageType, PAGE_TYPE_FACH) } : page
    ));
    applyEditorStructure(questions, nextPages);
  };

  const buildOrderedQuestionFlowIds = useCallback(() => {
    const customPages = [...questionPages].sort((a, b) => (a.order || 0) - (b.order || 0));
    const defaultOrder = clampDefaultQuestionsPageOrder(defaultQuestionsPageOrder, questionPages.length);
    const totalQuestionPages = customPages.length + 1;
    const orderedIds = [];
    let customCursor = 0;

    for (let position = 1; position <= totalQuestionPages; position += 1) {
      if (position === defaultOrder) {
        orderedIds.push(DEFAULT_QUESTIONS_PAGE_ID);
        continue;
      }
      const page = customPages[customCursor];
      if (page) orderedIds.push(page.id);
      customCursor += 1;
    }

    return orderedIds;
  }, [questionPages, defaultQuestionsPageOrder]);

  const applyOrderedQuestionFlowIds = useCallback((orderedIds) => {
    if (!Array.isArray(orderedIds) || orderedIds.length === 0) return;

    const pageMap = new Map(questionPages.map((page) => [page.id, page]));
    const orderedCustomPages = [];
    let nextDefaultOrder = 1;

    orderedIds.forEach((id, index) => {
      if (id === DEFAULT_QUESTIONS_PAGE_ID) {
        nextDefaultOrder = index + 1;
        return;
      }
      const page = pageMap.get(id);
      if (page) orderedCustomPages.push(page);
    });

    const nextPages = orderedCustomPages.map((page, index) => ({ ...page, order: index + 1 }));
    applyEditorStructure(questions, nextPages);
    setDefaultQuestionsPageOrder(clampDefaultQuestionsPageOrder(nextDefaultOrder, nextPages.length));
  }, [questionPages, questions, applyEditorStructure]);

  const moveQuestionPage = (pageId, direction) => {
    const orderedIds = buildOrderedQuestionFlowIds();
    const currentIndex = orderedIds.findIndex((id) => id === pageId);
    if (currentIndex === -1) return;

    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= orderedIds.length) return;

    const reorderedIds = [...orderedIds];
    const [movedId] = reorderedIds.splice(currentIndex, 1);
    reorderedIds.splice(targetIndex, 0, movedId);
    applyOrderedQuestionFlowIds(reorderedIds);
  };

  const moveDefaultQuestionsPage = (direction) => {
    moveQuestionPage(DEFAULT_QUESTIONS_PAGE_ID, direction);
  };

  const removeQuestionPage = (pageId) => {
    if (questionPages.length <= 1) {
      setError('Mindestens eine Seite muss vorhanden sein.');
      return;
    }

    const pageQuestionsCount = questions.filter((question) => question.pageId === pageId).length;
    if (pageQuestionsCount > 0 && !window.confirm('Diese Seite enthält Fragen. Wirklich löschen?')) {
      return;
    }

    const remainingPages = questionPages.filter((page) => page.id !== pageId);
    const fallbackPageId = remainingPages[0]?.id;
    const remappedQuestions = questions.map((question) => (
      question.pageId === pageId ? { ...question, pageId: fallbackPageId } : question
    ));
    applyEditorStructure(remappedQuestions, remainingPages);
  };

  const moveQuestionToPage = (questionId, pageId) => {
    const updatedQuestions = questions.map((question) => (
      question.id === questionId ? { ...question, pageId } : question
    ));
    applyEditorStructure(updatedQuestions, questionPages);
  };

  // Splices a reordered page list back into the flat question array, keeping the
  // page's questions where they already sat relative to the other pages.
  const applyReorderedPageQuestions = (pageId, reorderedPageQuestions) => {
    const nextQuestions = [];
    let inserted = false;
    questions.forEach((question) => {
      if (question.pageId !== pageId) {
        nextQuestions.push(question);
        return;
      }
      if (!inserted) {
        nextQuestions.push(...reorderedPageQuestions);
        inserted = true;
      }
    });

    applyEditorStructure(nextQuestions, questionPages);
  };

  const moveQuestionWithinPage = (pageId, fromIndex, toIndex) => {
    const pageQuestions = questions.filter((question) => question.pageId === pageId);
    const reorderedPageQuestions = reorderQuestionsByDrag(pageQuestions, fromIndex, toIndex);
    if (reorderedPageQuestions === pageQuestions) return;

    applyReorderedPageQuestions(pageId, reorderedPageQuestions);
  };

  // Button-based reordering: moves a question one slot, taking its conditional
  // sub-questions along. Drag-and-drop keeps using the flat splice above.
  const moveQuestionGroupWithinPage = (pageId, index, direction) => {
    const pageQuestions = questions.filter((question) => question.pageId === pageId);
    const reorderedPageQuestions = moveQuestionInList(pageQuestions, index, direction);
    if (reorderedPageQuestions === pageQuestions) return;

    applyReorderedPageQuestions(pageId, reorderedPageQuestions);
  };

  const moveDefaultQuestionWithinList = (fromIndex, toIndex) => {
    const reorderedDefaults = reorderQuestionsByDrag(defaultQuestions, fromIndex, toIndex);
    if (reorderedDefaults === defaultQuestions) return;
    setDefaultQuestions(reorderedDefaults);
  };

  const moveDefaultQuestionGroup = (index, direction) => {
    const reorderedDefaults = moveQuestionInList(defaultQuestions, index, direction);
    if (reorderedDefaults === defaultQuestions) return;
    setDefaultQuestions(reorderedDefaults);
  };

  // --- Derived state ---
  const isIntroPageSelected = selectedPageId === INTRO_PAGE_ID;
  const isDefaultQuestionsPageSelected = selectedPageId === DEFAULT_QUESTIONS_PAGE_ID;
  const normalizedDefaultQuestionsPageOrder = useMemo(
    () => clampDefaultQuestionsPageOrder(defaultQuestionsPageOrder, questionPages.length),
    [defaultQuestionsPageOrder, questionPages.length]
  );
  const orderedEditorQuestionPages = useMemo(() => {
    const customPages = [...questionPages].sort((a, b) => (a.order || 0) - (b.order || 0));
    const totalQuestionPages = customPages.length + 1;
    const orderedItems = [];
    let customCursor = 0;

    for (let position = 1; position <= totalQuestionPages; position += 1) {
      if (position === normalizedDefaultQuestionsPageOrder) {
        orderedItems.push({
          type: 'default',
          id: DEFAULT_QUESTIONS_PAGE_ID,
          questionFlowOrder: position,
          overallOrder: position + 1
        });
        continue;
      }

      const page = customPages[customCursor];
      if (page) {
        orderedItems.push({
          type: 'custom',
          id: page.id,
          page,
          questionFlowOrder: position,
          overallOrder: position + 1
        });
      }
      customCursor += 1;
    }

    return orderedItems;
  }, [questionPages, normalizedDefaultQuestionsPageOrder]);

  /** The open page's number, the same one the rail prints on its badge. */
  const activePageNumber = useMemo(() => {
    if (isIntroPageSelected) return 1;
    const entry = orderedEditorQuestionPages.find((item) =>
      item.type === 'default' ? isDefaultQuestionsPageSelected : item.page?.id === selectedPageId
    );
    return entry?.overallOrder ?? null;
  }, [isIntroPageSelected, isDefaultQuestionsPageSelected, selectedPageId, orderedEditorQuestionPages]);

  const activePage = useMemo(() => {
    if (isIntroPageSelected || isDefaultQuestionsPageSelected) return null;
    return questionPages.find((page) => page.id === selectedPageId) || questionPages[0] || null;
  }, [questionPages, selectedPageId, isIntroPageSelected, isDefaultQuestionsPageSelected]);

  const activePageQuestions = useMemo(
    () => questions.filter((question) => question.pageId === activePage?.id),
    [questions, activePage?.id]
  );
  const activePageType = normalizeCustomPageType(activePage?.pageType, PAGE_TYPE_FACH);
  const isBasisPageSelected = Boolean(activePage && activePageType === PAGE_TYPE_BASIS);
  const isFachPageSelected = Boolean(activePage && activePageType === PAGE_TYPE_FACH);
  const totalFormalQuestions = defaultQuestions.length;

  const questionsWithPageMeta = useCallback((sourceQuestions) => {
    const orderedPages = [...questionPages].sort((a, b) => (a.order || 0) - (b.order || 0));
    const pageLookup = new Map(
      orderedPages.map((page, index) => [page.id, {
        title: resolvePageTitle(page.title, `Seite ${index + 1}`),
        description: page.description || '',
        order: index + 1,
        pageType: normalizeCustomPageType(page.pageType, inferCustomPageTypeByIndex(index))
      }])
    );
    const fallbackPageId = orderedPages[0]?.id;

    return (sourceQuestions || []).map((question) => {
      const pageId = question.pageId && pageLookup.has(question.pageId) ? question.pageId : fallbackPageId;
      const pageMeta = pageLookup.get(pageId) || { title: 'Seite 1', description: '', order: 1, pageType: PAGE_TYPE_BASIS };
      return {
        ...question,
        pageId,
        pageTitle: pageMeta.title,
        pageDescription: pageMeta.description,
        pageOrder: pageMeta.order,
        pageType: pageMeta.pageType
      };
    });
  }, [questionPages]);

  // --- Questions CRUD hook ---
  const {
    addQuestion, updateQuestion, removeQuestion, setQuestionConditional,
    handleOptionChange, handleOptionScoreChange, addOption, removeOption,
    addDefaultQuestion, updateDefaultQuestion, removeDefaultQuestion, setDefaultQuestionConditional,
    handleDefaultOptionChange, handleDefaultOptionScoreChange, addDefaultOption, removeDefaultOption,
    normalizeQuestionConditionals
  } = useQuestionsCRUD({
    questions, defaultQuestions, setDefaultQuestions,
    questionPages, selectedPageId,
    applyEditorStructure, error, setError
  });

  // --- Templates hook ---
  const {
    templates,
    templateName, setTemplateName,
    selectedTemplateId, setSelectedTemplateId,
    saveAsTemplate, saveTemplateData, loadFromTemplate, applyTemplateToEmptyPages, deleteTemplateById,
    renameTemplateById,
    toggleTemplateArchivedById,
    basicQuestionTemplates,
    basicTemplateName, setBasicTemplateName,
    selectedBasicTemplateId, setSelectedBasicTemplateId,
    saveBasicQuestionTemplate, loadBasicQuestionTemplate, deleteBasicQuestionTemplateById,
    fachQuestionTemplates,
    fachTemplateName, setFachTemplateName,
    selectedFachTemplateId, setSelectedFachTemplateId,
    saveFachQuestionTemplate, loadFachQuestionTemplate, deleteFachQuestionTemplateById,
    defaultQuestionTemplates,
    defaultTemplateName, setDefaultTemplateName,
    selectedDefaultTemplateId, setSelectedDefaultTemplateId,
    saveDefaultQuestionTemplate, loadDefaultQuestionTemplate,
    deleteDefaultQuestionTemplateById, confirmDeleteDefaultQuestionTemplate,
    defaultFormalTemplateId,
    defaultBasisTemplateId,
    defaultFachTemplateId,
    setFormalTemplateAsDefault, resetFormalTemplateDefault,
    setBasisTemplateAsDefault, resetBasisTemplateDefault,
    setFachTemplateAsDefault, resetFachTemplateDefault,
    showTemplateConfirmModal, setShowTemplateConfirmModal,
    pendingTemplateData, setPendingTemplateData,
    showDefaultTemplateConfirmModal, setShowDefaultTemplateConfirmModal,
    pendingDefaultTemplateData, setPendingDefaultTemplateData,
    resetEditorToDefaults,
    configuredFormalTemplateName,
    configuredBasisTemplateName,
    configuredFachTemplateName
  } = useEditorTemplates({
    questions,
    defaultQuestions, setDefaultQuestions,
    questionPages,
    applyEditorStructure,
    questionsWithPageMeta,
    error, setError,
    showNotification,
    setTitle,
    setIntroText, setOutroText,
    setCancelText, setCancelOutroText,
    setIntroWelcomeText, setIntroOverviewText, setIntroAfterSectionsText,
    setDefaultSectionTitle, setDefaultSectionDescription,
    setReviewSectionTitle, setReviewSectionDescription,
    setIntroSectionsText, setIntroTimerText, setIntroStartButtonText,
    setAutoSubmitEnabled, setAllowMultipleSubmissions,
    setDefaultQuestionsPageOrder,
    setScoringPrompt, setScoringTotalScore, setPageMaxScores,
    assignedTo, setAssignedTo,
    defaultIntroText, defaultOutroText,
    activePage, isBasisPageSelected, isFachPageSelected,
    normalizedDefaultQuestionsPageOrder,
    sectionsTextForSave,
    getFullEditorState: () => ({
      title, introText, outroText, cancelText, cancelOutroText,
      introWelcomeText, introOverviewText, introAfterSectionsText,
      defaultSectionTitle, defaultSectionDescription,
      reviewSectionTitle, reviewSectionDescription,
      introTimerText, introStartButtonText, autoSubmitEnabled,
      allowMultipleSubmissions,
      scoringPrompt, scoringTotalScore, pageMaxScores
    })
  });

  const questionnaireTemplates = useMemo(
    () => (templates || []).filter((template) => !template?.intro_page?.templateKind),
    [templates]
  );

  // --- Drag and drop hook ---
  const {
    draggedIndex, setDraggedIndex, dragOverIndex, setDragOverIndex,
    draggedOptionIndex, dragOverOptionIndex,
    defaultDraggedIndex, setDefaultDraggedIndex, defaultDragOverIndex, setDefaultDragOverIndex,
    draggedPageCardId, dragOverPageCardId,
    handleDragStart, handleDragOver, handleDragLeave, handleDrop, handleDragEnd,
    handleDefaultDragStart, handleDefaultDragOver, handleDefaultDragLeave, handleDefaultDrop, handleDefaultDragEnd,
    handleOptionDragStart, handleOptionDragOver, handleOptionDragEnd,
    handleDefaultOptionDragStart, handleDefaultOptionDragOver, handleDefaultOptionDragEnd,
    handlePageCardDragStart, handlePageCardDragOver, handlePageCardDragLeave,
    handlePageCardDrop, handlePageCardDragEnd, handlePageCardSelect
  } = useEditorDragDrop({
    questions, activePageQuestions, activePage,
    defaultQuestions, questionPages,
    applyEditorStructure, normalizeQuestionConditionals,
    setDefaultQuestions, updateQuestion, updateDefaultQuestion,
    buildOrderedQuestionFlowIds, applyOrderedQuestionFlowIds,
    setSelectedPageId
  });

  // --- AI hook ---
  const { isLoading, generateAIQuestions, generateAITitle } = useEditorAI({
    setError, questions, setTitle,
    aiJobTitle, aiShouldFillDescriptions,
    applyEditorStructure, setDefaultQuestionsPageOrder
  });

  // --- Questionnaire data snapshot ---
  const getQuestionnaireData = useCallback(() => ({
    title,
    questions,
    questionPages,
    introText,
    outroText,
    introWelcomeText,
    introOverviewText,
    introAfterSectionsText,
    defaultSectionTitle,
    defaultSectionDescription,
    reviewSectionTitle,
    reviewSectionDescription,
    introSectionsText: sectionsTextForSave,
    introTimerText,
    introStartButtonText,
    cancelText,
    cancelOutroText,
    autoSubmitEnabled,
    allowMultipleSubmissions,
    defaultQuestions,
    defaultQuestionsPageOrder: normalizedDefaultQuestionsPageOrder,
    showIntroField,
    showOutroField,
    showCancelField,
    showIntroPageFields,
    showDefaultQuestionsFields,
    aiJobTitle,
    aiShouldFillDescriptions,
    templateName,
    selectedTemplateId,
    basicTemplateName,
    selectedBasicTemplateId,
    defaultTemplateName,
    selectedDefaultTemplateId,
    selectedPageId,
    scoringTotalScore,
    scoringPrompt,
    pageMaxScores
  }), [title, questions, questionPages, introText, outroText, introWelcomeText, introOverviewText, introAfterSectionsText, defaultSectionTitle, defaultSectionDescription, reviewSectionTitle, reviewSectionDescription, sectionsTextForSave, introTimerText, introStartButtonText, cancelText, cancelOutroText, autoSubmitEnabled, allowMultipleSubmissions, defaultQuestions, normalizedDefaultQuestionsPageOrder, showIntroField, showOutroField, showCancelField, showIntroPageFields, showDefaultQuestionsFields, aiJobTitle, aiShouldFillDescriptions, templateName, selectedTemplateId, basicTemplateName, selectedBasicTemplateId, defaultTemplateName, selectedDefaultTemplateId, selectedPageId, scoringTotalScore, scoringPrompt, pageMaxScores]);

  // --- All-in-one questionnaire export (structure + full point allocation) ---
  const handleExportQuestionnaire = useCallback(() => {
    const data = getQuestionnaireData();
    const stripHtml = (s) => (typeof s === 'string' ? s.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim() : '');

    const summarizeQuestion = (q, pageLabel) => {
      const entry = {
        id: q.id,
        page: pageLabel,
        type: q.type,
        text: stripHtml(q.text),
        important: !!q.important,
        maxScore: Number(q.maxScore) || 0
      };
      if (Array.isArray(q.options) && q.options.length > 0) {
        entry.options = q.options.map((opt, i) => ({
          label: stripHtml(typeof opt === 'string' ? opt : (opt?.label ?? String(opt ?? ''))),
          score: Array.isArray(q.optionScores) ? (Number(q.optionScores[i]) || 0) : null
        }));
      }
      return entry;
    };

    const summaryQuestions = [];
    (data.defaultQuestions || [])
      .filter((q) => q.id !== 'system_name')
      .forEach((q) => summaryQuestions.push(summarizeQuestion(q, 'Rahmendaten & Qualifikation')));
    (data.questionPages || []).forEach((page) => {
      (page.questions || []).forEach((q) => summaryQuestions.push(summarizeQuestion(q, stripHtml(page.title) || page.id)));
    });

    const sumOfQuestionMaxScores = summaryQuestions.reduce((sum, q) => sum + (q.maxScore || 0), 0);

    const exportObject = {
      exportVersion: 1,
      exportedAt: new Date().toISOString(),
      title: data.title,
      scoring: {
        configuredTotalScore: Number(data.scoringTotalScore) || 0,
        sumOfQuestionMaxScores,
        scoringPrompt: data.scoringPrompt || '',
        pageMaxScores: data.pageMaxScores || {}
      },
      scoringSummary: summaryQuestions,
      questionnaire: data
    };

    const json = JSON.stringify(exportObject, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const safeTitle = (data.title || 'fragebogen')
      .replace(/[^a-z0-9]+/gi, '_')
      .replace(/^_+|_+$/g, '')
      .toLowerCase() || 'fragebogen';
    const dateStr = new Date().toISOString().slice(0, 10);
    link.href = url;
    link.download = `fragebogen_${safeTitle}_${dateStr}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [getQuestionnaireData]);

  // --- Restore full state (used by auto-save version restore) ---
  const restoreFullState = useCallback((data) => {
    if (!data) return;

    setTitle(data.title || '');
    {
      const restoredQuestions = data.questions ? data.questions.map(q => ({ ...q, id: Date.now() + Math.random() })) : [];
      const restoredPages = data.questionPages || data.intro_page?.questionPages || questionPages;
      const normalized = applyEditorStructure(restoredQuestions, restoredPages);
      if (data.selectedPageId && normalized.questionPages.some((page) => page.id === data.selectedPageId)) {
        setSelectedPageId(data.selectedPageId);
      }
      const restoredDefaultOrder = data.defaultQuestionsPageOrder ?? data.intro_page?.defaultQuestionsPageOrder;
      if (restoredDefaultOrder !== undefined) {
        setDefaultQuestionsPageOrder(clampDefaultQuestionsPageOrder(restoredDefaultOrder, normalized.questionPages.length));
      } else {
        setDefaultQuestionsPageOrder(clampDefaultQuestionsPageOrder(normalized.questionPages.length + 1, normalized.questionPages.length));
      }
    }
    setIntroText(data.introText || defaultIntroText);
    setOutroText(data.outroText || defaultOutroText);
    setCancelText(data.cancelText || 'Du hast gemerkt, dass wir vielleicht nicht die richtige Passung sind? Kein Problem — das ist für beide Seiten wichtig! Hier kannst du uns einfach mitteilen, dass du deine Bewerbung zurückziehen möchtest.');
    setCancelOutroText(data.cancelOutroText || 'Vielen Dank für deine Zeit! Du hast deine Bewerbung erfolgreich abgesagt. Falls du dich doch nochmal entscheidest, kannst du gerne eine neue Bewerbung einreichen.');

    if (data.intro_page) {
      const legacySectionConfig = extractLegacySectionConfig(data.intro_page.sectionsText || data.introSectionsText || '');
      setIntroWelcomeText(data.intro_page.welcomeText || '');
      setIntroOverviewText(data.intro_page.overviewText || data.introOverviewText || '');
      setIntroAfterSectionsText(data.intro_page.afterSectionsText || data.introAfterSectionsText || '');
      setDefaultSectionTitle(data.intro_page.defaultSectionTitle ?? data.defaultSectionTitle ?? legacySectionConfig.defaultSectionTitle);
      setDefaultSectionDescription(data.intro_page.defaultSectionDescription ?? data.defaultSectionDescription ?? legacySectionConfig.defaultSectionDescription);
      setReviewSectionTitle(data.intro_page.reviewSectionTitle ?? data.reviewSectionTitle ?? legacySectionConfig.reviewSectionTitle);
      setReviewSectionDescription(data.intro_page.reviewSectionDescription ?? data.reviewSectionDescription ?? legacySectionConfig.reviewSectionDescription);
      setIntroSectionsText(data.intro_page.sectionsText || '');
      setIntroTimerText(data.intro_page.timerText || '');
      setIntroStartButtonText(data.intro_page.startButtonText || '');
      setAutoSubmitEnabled(data.intro_page.autoSubmitEnabled !== undefined ? data.intro_page.autoSubmitEnabled : false);
      setAllowMultipleSubmissions(data.intro_page.allowMultipleSubmissions !== undefined
        ? data.intro_page.allowMultipleSubmissions
        : (data.allowMultipleSubmissions !== undefined ? data.allowMultipleSubmissions : false));
    } else {
      const legacySectionConfig = extractLegacySectionConfig(data.introSectionsText || '');
      setIntroWelcomeText(data.introWelcomeText || '');
      setIntroOverviewText(data.introOverviewText || '');
      setIntroAfterSectionsText(data.introAfterSectionsText || '');
      setDefaultSectionTitle(data.defaultSectionTitle ?? legacySectionConfig.defaultSectionTitle);
      setDefaultSectionDescription(data.defaultSectionDescription ?? legacySectionConfig.defaultSectionDescription);
      setReviewSectionTitle(data.reviewSectionTitle ?? legacySectionConfig.reviewSectionTitle);
      setReviewSectionDescription(data.reviewSectionDescription ?? legacySectionConfig.reviewSectionDescription);
      setIntroSectionsText(data.introSectionsText || '');
      setIntroTimerText(data.introTimerText || '');
      setIntroStartButtonText(data.introStartButtonText || '');
      setAutoSubmitEnabled(data.autoSubmitEnabled !== undefined ? data.autoSubmitEnabled : false);
      setAllowMultipleSubmissions(data.allowMultipleSubmissions !== undefined ? data.allowMultipleSubmissions : false);
    }

    if (data.defaultQuestions && Array.isArray(data.defaultQuestions) && data.defaultQuestions.length > 0) {
      const hasNameQuestion = data.defaultQuestions.some((question) => question?.id === NAME_QUESTION_SEED.id);
      setDefaultQuestions(hasNameQuestion ? data.defaultQuestions : [NAME_QUESTION_SEED, ...data.defaultQuestions]);
    } else {
      setDefaultQuestions([NAME_QUESTION_SEED]);
    }
    if (data.scoringTotalScore !== undefined) {
      const parsedTotalScore = Number(data.scoringTotalScore);
      setScoringTotalScore(Number.isFinite(parsedTotalScore) && parsedTotalScore > 0 ? Math.round(parsedTotalScore) : 100);
    }
    if (data.scoringPrompt !== undefined) {
      setScoringPrompt(data.scoringPrompt || '');
    }
    if (data.pageMaxScores !== undefined) {
      setPageMaxScores(data.pageMaxScores && typeof data.pageMaxScores === 'object' ? data.pageMaxScores : {});
    }
    if (data.showIntroField !== undefined) setShowIntroField(data.showIntroField);
    if (data.showOutroField !== undefined) setShowOutroField(data.showOutroField);
    if (data.showIntroPageFields !== undefined) setShowIntroPageFields(data.showIntroPageFields);
    if (data.showCancelField !== undefined) setShowCancelField(data.showCancelField);
    if (data.showDefaultQuestionsFields !== undefined) setShowDefaultQuestionsFields(data.showDefaultQuestionsFields);
    if (data.aiJobTitle !== undefined) setAiJobTitle(data.aiJobTitle);
    if (data.aiShouldFillDescriptions !== undefined) setAiShouldFillDescriptions(data.aiShouldFillDescriptions);
    if (data.templateName !== undefined) setTemplateName(data.templateName);
    if (data.selectedTemplateId !== undefined) setSelectedTemplateId(data.selectedTemplateId);
    if (data.basicTemplateName !== undefined) setBasicTemplateName(data.basicTemplateName);
    if (data.selectedBasicTemplateId !== undefined) setSelectedBasicTemplateId(data.selectedBasicTemplateId);
    if (data.defaultTemplateName !== undefined) setDefaultTemplateName(data.defaultTemplateName);
    if (data.selectedDefaultTemplateId !== undefined) setSelectedDefaultTemplateId(data.selectedDefaultTemplateId);

    setError(null);
  }, [applyEditorStructure, questionPages, defaultIntroText, defaultOutroText, setError, setTemplateName, setSelectedTemplateId, setBasicTemplateName, setSelectedBasicTemplateId, setDefaultTemplateName, setSelectedDefaultTemplateId]);

  // --- Import (AI-generated / exported questionnaire JSON) ---
  const openImportModal = () => {
    setImportText('');
    setImportError('');
    setShowImportModal(true);
  };

  const handleImportFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setImportText(typeof reader.result === 'string' ? reader.result : '');
      setImportError('');
    };
    reader.onerror = () => setImportError('Die Datei konnte nicht gelesen werden.');
    reader.readAsText(file);
    event.target.value = '';
  };

  const handleDownloadSchema = () => {
    const json = JSON.stringify(QUESTIONNAIRE_IMPORT_SCHEMA, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'questionnaire-import.schema.json';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleCopyAiPrompt = async () => {
    try {
      await navigator.clipboard.writeText(AI_IMPORT_PROMPT);
      showNotification('KI-Prompt kopiert');
    } catch (e) {
      setImportError('Kopieren fehlgeschlagen — bitte manuell kopieren.');
    }
  };

  const handleImportConfirm = () => {
    let parsed;
    try {
      parsed = JSON.parse(importText);
    } catch (e) {
      setImportError(`Ungültiges JSON: ${e.message}`);
      return;
    }
    const result = parseQuestionnaireImport(parsed);
    if (!result.ok) {
      setImportError(result.errors.join('\n'));
      return;
    }
    // Fragen, die es schon gibt, behalten ihre ID. Sonst zeigt die
    // Ergebnisse-Seite die Antworten bestehender Bewerbungen nicht mehr an —
    // sie hängen an der alten ID und finden ihre Frage nicht wieder.
    const { data: importedData, kept, fresh } = preserveQuestionIds(result.data, [
      ...defaultQuestions,
      ...questions
    ]);

    restoreFullState(importedData);
    setShowImportModal(false);
    setImportText('');
    setImportError('');
    const label = result.format === 'export' ? 'Export-Datei' : 'KI-Fragebogen';
    const hint = result.warnings && result.warnings.length ? ` · ${result.warnings.length} Hinweis(e)` : '';
    const idHint = kept > 0 ? ` · ${kept} Frage(n) behalten ihre ID, ${fresh} neu` : '';
    showNotification(`${label} importiert${idHint}${hint}`);
  };

  // --- Auto-save hook ---
  const {
    versions, lastSaveTime,
    selectedVersionId, setSelectedVersionId,
    saveVersion, restoreVersion, deleteVersion
  } = useAutoSave({
    getQuestionnaireData,
    restoreFullState,
    showNotification
  });

  const {
    containerRef: layoutRef,
    railRef,
    isResizing: isRailResizing,
    containerStyle: layoutStyle,
    resizerProps: railResizerProps
  } = useResizableRail();

  // Jump from the sidebar question list to a question's editor card. The
  // target page is selected first; the scroll runs in an effect so the page
  // content is in the DOM before we look up the question element.
  const [questionScrollTarget, setQuestionScrollTarget] = useState(null);
  const jumpToQuestion = useCallback((pageId, questionId) => {
    setSelectedPageId(pageId);
    setQuestionScrollTarget({ questionId, requestedAt: Date.now() });
  }, []);

  useEffect(() => {
    if (!questionScrollTarget) return undefined;

    let innerFrame = null;
    let highlightTimer = null;

    // Two frames, not one. The first paints the newly selected page; the
    // second lets its auto-sizing textareas finish growing. Measuring after
    // one frame reads a layout that is still moving, which is how the target
    // ends up somewhere other than where it was aimed.
    const outerFrame = requestAnimationFrame(() => {
      innerFrame = requestAnimationFrame(() => {
        const selector = `[data-question-id="${CSS.escape(String(questionScrollTarget.questionId))}"]`;
        const el = document.querySelector(selector);
        if (!el) return;

        // No offset arithmetic here: scroll-padding-top on <html> keeps the
        // sticky bar clear for every scroll in the app (see input.css).
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });

        el.style.outline = '2px solid #4ade80';
        el.style.outlineOffset = '3px';
        el.style.borderRadius = '0.75rem';
        highlightTimer = setTimeout(() => {
          el.style.outline = '';
          el.style.outlineOffset = '';
          el.style.borderRadius = '';
        }, 1800);
      });
    });

    return () => {
      cancelAnimationFrame(outerFrame);
      if (innerFrame) cancelAnimationFrame(innerFrame);
      if (highlightTimer) clearTimeout(highlightTimer);
    };
  }, [questionScrollTarget]);

  // --- Effects ---
  useEffect(() => {
    if (!selectedPageId) {
      setSelectedPageId(INTRO_PAGE_ID);
      return;
    }
    if (selectedPageId === INTRO_PAGE_ID || selectedPageId === DEFAULT_QUESTIONS_PAGE_ID) {
      return;
    }
    if (!questionPages.some((page) => page.id === selectedPageId)) {
      setSelectedPageId(questionPages[0]?.id || null);
    }
  }, [questionPages, selectedPageId]);

  useEffect(() => {
    const clampedOrder = clampDefaultQuestionsPageOrder(defaultQuestionsPageOrder, questionPages.length);
    if (clampedOrder !== defaultQuestionsPageOrder) {
      setDefaultQuestionsPageOrder(clampedOrder);
    }
  }, [defaultQuestionsPageOrder, questionPages.length]);

  // Globally prevent drag-start from editable/form controls across the editor.
  useEffect(() => {
    const preventDragFromInteractiveFields = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('input, textarea, select, option, button, [contenteditable="true"], [role="textbox"]')) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    document.addEventListener('dragstart', preventDragFromInteractiveFields, true);
    return () => {
      document.removeEventListener('dragstart', preventDragFromInteractiveFields, true);
    };
  }, []);

  // Load internal accounts for the "Zuständig" picker.
  useEffect(() => {
    let cancelled = false;
    dbService.getUsers().then((list) => {
      if (cancelled) return;
      setAccounts(list);
    });
    return () => { cancelled = true; };
  }, []);

  // Load draft data on component mount
  useEffect(() => {
    if (hasLoadedDraftRef.current) return;
    hasLoadedDraftRef.current = true;

    const loadDraft = () => {
      try {
        const saved = localStorage.getItem('questionnaire_current_draft');
        if (saved) {
          const draftData = JSON.parse(saved);

          const draftTime = localStorage.getItem('questionnaire_draft_timestamp');
          if (draftTime) {
            const timeDiff = Date.now() - parseInt(draftTime);
            const hoursDiff = timeDiff / (1000 * 60 * 60);

            if (hoursDiff < 24) {
              restoreFullState(draftData);
              if (draftData.selectedPageId !== undefined) setSelectedPageId(draftData.selectedPageId);
            }
          }
        }
      } catch (error) {
        console.warn('Failed to load draft data:', error);
      }
    };

    loadDraft();
  }, [restoreFullState]);

  const prepareQuestionsForSave = async (sourceQuestions, questionnaireId = null) => {
    const prepared = await Promise.all(sourceQuestions.map(async (q) => {
      if (q.type === 'image' && q.file) {
        try {
          const uploadedImage = await imageUploadService.uploadQuestionnaireImage(q.file, {
            title: q.text || 'Question Image',
            description: q.description || '',
            questionnaire_id: questionnaireId || '',
            question_id: q.id.toString()
          });

          return {
            ...q,
            imageUrl: uploadedImage.url,
            imageId: uploadedImage.id,
            imageDataUrl: null,
            file: null
          };
        } catch (error) {
          console.error('Failed to upload image:', error);
          try {
            const imageDataUrl = await compressImageFile(q.file);
            return { ...q, imageDataUrl };
          } catch (compressError) {
            console.error('Failed to compress image:', compressError);
            return q;
          }
        }
      }
      return q;
    }));
    return prepared;
  };

  const generateLinkInternal = async () => {
    if (!title.trim()) { setError("Bitte einen Titel eingeben."); return; }
    if (questions.length === 0) { setError("Bitte mindestens eine Frage hinzufügen."); return; }

    const sourceQuestionsWithPageMeta = questionsWithPageMeta(questions);

    for (const q of sourceQuestionsWithPageMeta) {
      if (q.type !== 'image' && q.type !== 'ranking' && (!q.text || !q.text.trim())) {
        setError("Bitte Fragetext für alle Text-/Choice-Fragen ausfüllen.");
        return;
      }
      if (q.type === 'image' && !q.imageDataUrl && !q.imageUrl && !q.file) {
        setError("Bitte für Bildfragen eine Bilddatei hochladen.");
        return;
      }
      if (q.type === 'ranking' && (!q.options || q.options.length < 2)) {
        setError("Bitte mindestens 2 Aussagen für Ranking-Fragen hinzufügen.");
        return;
      }
    }
    for (const q of sourceQuestionsWithPageMeta) {
      if (q.type === 'image' && q.imageDataUrl) {
        const bytes = dataUrlToBytes(q.imageDataUrl);
        const isGif = q.imageDataUrl.startsWith('data:image/gif');
        const limit = isGif ? MAX_GIF_BYTES : MAX_IMAGE_BYTES;
        if (bytes > limit) {
          const kb = Math.round(bytes / 1024);
          const maxKb = Math.round(limit / 1024);
          setError(`Bild ist zu groß (${kb} KB). Limit: ${maxKb} KB${isGif ? ' (GIF)' : ''}.`);
          return;
        }
      }
    }

    try {
      const introPageData = {
        welcomeText: introWelcomeText,
        overviewText: introOverviewText,
        afterSectionsText: introAfterSectionsText,
        defaultSectionTitle,
        defaultSectionDescription,
        reviewSectionTitle,
        reviewSectionDescription,
        sectionsText: sectionsTextForSave,
        timerText: introTimerText,
        startButtonText: introStartButtonText,
        autoSubmitEnabled: autoSubmitEnabled,
        allowMultipleSubmissions: allowMultipleSubmissions,
        defaultQuestionsPageOrder: normalizedDefaultQuestionsPageOrder,
        questionPages: questionPages.map((page, index) => ({
          id: page.id,
          title: resolvePageTitle(page.title, `Seite ${index + 1}`),
          description: page.description || '',
          order: index + 1,
          pageType: normalizeCustomPageType(page.pageType, inferCustomPageTypeByIndex(index))
        }))
      };

      const questionnaireData = {
        title,
        questions: [],
        questionPages: introPageData.questionPages,
        defaultQuestions,
        defaultQuestionsPageOrder: normalizedDefaultQuestionsPageOrder,
        introText,
        outroText,
        introWelcomeText,
        introOverviewText,
        introAfterSectionsText,
        defaultSectionTitle,
        defaultSectionDescription,
        reviewSectionTitle,
        reviewSectionDescription,
        introSectionsText: sectionsTextForSave,
        introTimerText,
        introStartButtonText,
        cancelText,
        cancelOutroText,
        autoSubmitEnabled,
        allowMultipleSubmissions,
        assignedTo: assignedTo || undefined,
        intro_page: introPageData,
        scoring_config: JSON.stringify({
          totalScore: scoringTotalScore,
          scoringPrompt: scoringPrompt,
          pageMaxScores: pageMaxScores
        })
      };

      console.log('Creating questionnaire with data:', questionnaireData);

      let uniqueId;
      try {
        uniqueId = await dbService.createQuestionnaire(questionnaireData);
        console.log('Questionnaire created with ID:', uniqueId);
      } catch (error) {
        console.error('Failed to create questionnaire with full data, trying minimal data:', error);

        const minimalData = {
          title: questionnaireData.title,
          questions: JSON.stringify([]),
          defaultQuestions: JSON.stringify([]),
          introText: '',
          outroText: '',
          introWelcomeText: '',
          introOverviewText: '',
          introAfterSectionsText: '',
          introSectionsText: '',
          introTimerText: '',
          introStartButtonText: '',
          cancelText: '',
          cancelOutroText: '',
          autoSubmitEnabled: false,
          allowMultipleSubmissions: false,
          assignedTo: assignedTo || undefined,
          intro_page: null
        };

        uniqueId = await dbService.createQuestionnaire(minimalData);
        console.log('Questionnaire created with minimal data, ID:', uniqueId);
      }

      const finalPreparedQuestions = await prepareQuestionsForSave(sourceQuestionsWithPageMeta, uniqueId);

      const finalQuestionnaireData = {
        ...questionnaireData,
        questions: finalPreparedQuestions
      };

      await dbService.updateQuestionnaire(uniqueId, finalQuestionnaireData);

      const applicantBase = 'https://fragebogen.karrieresichern.de';
      let link = `${applicantBase}?page=fragebogen&id=${uniqueId}`;
      if (!allowMultipleSubmissions) {
        const accessToken = await dbService.createQuestionnaireAccessToken(uniqueId);
        link = `${link}&t=${encodeURIComponent(accessToken)}`;
      }

      const verifyQuestionnaire = await dbService.getQuestionnaire(uniqueId);
      if (!verifyQuestionnaire) {
        throw new Error(`Verifikation fehlgeschlagen: Fragebogen ${uniqueId} nicht im Speicher gefunden.`);
      }

      setGeneratedLink(link);
      setShowLinkModal(true);
      setError(null);
      showNotification('Gespeichert');
    } catch (error) {
      console.error('Error creating questionnaire:', error);
      setError(`Fehler beim Erstellen des Fragebogens: ${error.message}`);
    }
  };

  const generateLink = async () => {
    if (allowMultipleSubmissions) {
      setShowMultipleSubmissionWarningModal(true);
      return;
    }
    await generateLinkInternal();
  };

  // AI-powered scoring setup: assigns per-question maxScore and per-option scores in one batch
  const setupScoringWithAI = async () => {
    const allQuestions = [...defaultQuestions, ...questions];
    if (allQuestions.length === 0) {
      showNotification('Bitte zuerst Fragen hinzufügen.', 'error');
      return;
    }
    setIsSettingUpScoring(true);
    try {
      const fallbackQuestionMaxScore = Math.max(1, Math.round(scoringTotalScore / allQuestions.length));
      const questionnaireTitle = String(title || '').trim();
      let templateScoringPrompt = '';
      let templateAnalysisPrompt = '';

      if (questionnaireTitle) {
        try {
          const allTemplates = await dbService.getTemplates();
          const template = allTemplates.find((entry) => String(entry?.title || '').trim() === questionnaireTitle);
          templateScoringPrompt = String(template?.scoring_prompt || '').trim();
          templateAnalysisPrompt = String(template?.analysis_prompt || '').trim();
        } catch (templateLoadError) {
          console.warn('Could not load template prompts for scoring setup:', templateLoadError);
        }
      }

      let settingsSystemPrompt = '';
      let configuredPointScoringPrompt = '';
      try {
        const settings = await dbService.getSettings();
        if (!templateAnalysisPrompt) {
          settingsSystemPrompt = String(settings?.systemPrompt || '').trim();
        }
        configuredPointScoringPrompt = String(settings?.pointScoringPrompt || '').trim();
      } catch (settingsLoadError) {
        console.warn('Could not load settings for scoring setup:', settingsLoadError);
      }

      const explicitScoringPrompt = String(scoringPrompt || '').trim() || templateScoringPrompt;
      const analysisBasePrompt = templateAnalysisPrompt || settingsSystemPrompt;
      let effectiveScoringGuidelines = explicitScoringPrompt;
      if (analysisBasePrompt && explicitScoringPrompt) {
        effectiveScoringGuidelines = `${analysisBasePrompt}\n\nSPEZIFISCHE PUNKTEVERGABE:\n${explicitScoringPrompt}`;
      } else if (!effectiveScoringGuidelines) {
        effectiveScoringGuidelines = analysisBasePrompt;
      }
      if (!effectiveScoringGuidelines) {
        effectiveScoringGuidelines = 'Bewerte jede Antwort fair und objektiv.';
      }

      // Build batches: one per non-empty page. Each batch gets its own
      // sub-budget (explicit pageMaxScore if set, else proportional share) and
      // is sent to Gemini in parallel — wall-clock = slowest single call.
      const formatQuestionInfo = (q, indexInBatch, pageTitle) => {
        const qText = (q.text || '').replace(/<[^>]*>/g, '').trim() || `Frage ${indexInBatch}`;
        const hasOptions = isOptionScoringQuestion(q);
        const typeLabel = q.type === 'image' ? `image/${q.answerType || 'text'}` : q.type;
        let info = `Frage ${indexInBatch}: "${qText}" (ID: ${q.id}, Typ: ${typeLabel}, Seite: ${pageTitle})${q.important ? ' ⭐ WICHTIG – Diese Frage soll höher gewichtet werden!' : ''}`;
        if (hasOptions) {
          if (q.type === 'matrix') {
            const rowCount = (q.matrixMode === 'predefined' && q.rowLabels) ? q.rowLabels.length : '(dynamisch)';
            info += `\nSpaltenoptionen (jede Zeile wählt eine Spalte, Punkte addieren sich über ${rowCount} Zeilen): ${q.options.map((o, j) => `${j + 1}. "${o}"`).join(', ')}`;
          } else if (q.type === 'combobox') {
            const entryCount = (q.comboboxMode === 'predefined' && q.predefinedEntries) ? q.predefinedEntries.length : '(dynamisch)';
            info += `\nDropdown-Optionen (jeder Eintrag wählt eine Option, Punkte addieren sich über ${entryCount} Einträge): ${q.options.map((o, j) => `${j + 1}. "${o}"`).join(', ')}`;
          } else if (q.type === 'ranking') {
            info += `\nRanking-Optionen (Bewerber ordnet nach Priorität, Punkte bei korrektem Rang): ${q.options.map((o, j) => `${j + 1}. "${o}"`).join(', ')}`;
          } else {
            info += `\nOptionen: ${q.options.map((o, j) => `${j + 1}. "${o}"`).join(', ')}`;
          }
        }
        const existingMaxScore = Number(q?.maxScore);
        if (Number.isFinite(existingMaxScore) && existingMaxScore >= 0) {
          info += `\nAktuelle Maximalpunkte: ${Math.round(existingMaxScore)}`;
        }
        return info;
      };

      const pageGroups = orderedEditorQuestionPages
        .map((entry) => {
          const pageKey = entry.type === 'default' ? 'default' : entry.id;
          const pageTitle = entry.type === 'default'
            ? (defaultSectionTitle || 'Formale Fragen')
            : (entry.page?.title || `Seite ${entry.questionFlowOrder}`);
          const pageQuestions = entry.type === 'default'
            ? defaultQuestions
            : questions.filter((q) => q.pageId === entry.id);
          return { pageKey, pageTitle, pageQuestions };
        })
        .filter((g) => g.pageQuestions.length > 0);

      // Allocate per-batch budget. Explicit pageMaxScores win; remaining budget
      // is split proportionally by question count.
      let explicitBudgetTotal = 0;
      let unbudgetedQuestionTotal = 0;
      pageGroups.forEach((g) => {
        const raw = pageMaxScores[g.pageKey];
        const numeric = Number(raw);
        if (raw !== undefined && raw !== '' && Number.isFinite(numeric) && numeric >= 0) {
          explicitBudgetTotal += numeric;
        } else {
          unbudgetedQuestionTotal += g.pageQuestions.length;
        }
      });
      const remainingBudget = Math.max(0, scoringTotalScore - explicitBudgetTotal);

      const batches = pageGroups.map((g) => {
        const raw = pageMaxScores[g.pageKey];
        const numeric = Number(raw);
        const hasExplicit = raw !== undefined && raw !== '' && Number.isFinite(numeric) && numeric >= 0;
        const budget = hasExplicit
          ? numeric
          : (unbudgetedQuestionTotal > 0
              ? Math.round(remainingBudget * (g.pageQuestions.length / unbudgetedQuestionTotal))
              : 0);
        return { ...g, budget };
      });

      // Seed the progress UI with one step per batch.
      setScoringProgress({
        steps: batches.map((b) => ({
          id: b.pageKey,
          label: b.pageTitle,
          hint: `${b.pageQuestions.length} ${b.pageQuestions.length === 1 ? 'Frage' : 'Fragen'} · ${b.budget} Punkte`,
          status: 'pending',
          durationMs: null,
          startedAt: null
        }))
      });

      const schema = {
        type: 'ARRAY',
        items: {
          type: 'OBJECT',
          properties: {
            questionId: { type: 'STRING' },
            maxScore: { type: 'NUMBER' },
            optionScores: { type: 'ARRAY', items: { type: 'NUMBER' }, nullable: true }
          }
        }
      };

      const IMPORTANCE_MULTIPLIER = 1.5;

      // Each batch finalizes and commits its own page immediately on return,
      // so the user sees points light up page-by-page instead of waiting for
      // the slowest sibling. Concurrent commits use functional setState so
      // they merge cleanly with whatever state another batch just wrote.
      const callBatch = async (batch) => {
        const startedAt = Date.now();
        setScoringProgress((prev) => markStepStart(prev, batch.pageKey));
        const questionsInfo = batch.pageQuestions
          .map((q, idx) => formatQuestionInfo(q, idx + 1, batch.pageTitle))
          .join('\n\n');
        const batchPageBudgetInfo = `\n\nSeitenbudget: Die Summe der maxScore-Werte aller Fragen dieser Seite muss exakt ${batch.budget} Punkte ergeben.`;
        const prompt = renderPointScoringPrompt(configuredPointScoringPrompt, {
          guidelines: effectiveScoringGuidelines,
          totalScore: batch.budget,
          pageBudgets: batchPageBudgetInfo,
          questions: `--- Seite: ${batch.pageTitle} ---\n${questionsInfo}`
        });
        const payload = {
          contents: [{ role: 'user', parts: [{ text: prompt }] }],
          generationConfig: { responseMimeType: 'application/json', responseSchema: schema }
        };
        const response = await geminiFetch({ payload });
        if (!response.ok) throw new Error(`API-Fehler ${describeGeminiApiError(response.status, response.statusText)}`);
        const result = await response.json();
        const rawText = result?.candidates?.[0]?.content?.parts?.[0]?.text;
        if (!rawText) throw new Error('Leere KI-Antwort');
        const batchSuggestions = JSON.parse(rawText);
        if (!Array.isArray(batchSuggestions)) throw new Error('Ungültiges KI-Format für Punktesystem');

        const suggestionById = new Map();
        batchSuggestions.forEach((s) => {
          if (!s) return;
          const directId = String(s.questionId || '').trim();
          if (directId) suggestionById.set(directId, s);
        });

        const scoped = batch.pageQuestions.map((question) => {
          const suggestion = suggestionById.get(String(question.id));
          const suggestedMaxScore = Number(suggestion?.maxScore);
          const existingMaxScore = Number(question?.maxScore);
          const initialMaxScore = Number.isFinite(suggestedMaxScore) && suggestedMaxScore >= 0
            ? Math.round(suggestedMaxScore)
            : (Number.isFinite(existingMaxScore) && existingMaxScore >= 0
                ? Math.round(existingMaxScore)
                : fallbackQuestionMaxScore);
          const { optionScores: _unused, ...rest } = question;
          const next = { ...rest, maxScore: initialMaxScore };
          if (isOptionScoringQuestion(question)) {
            const suggestionScores = Array.isArray(suggestion?.optionScores) ? suggestion.optionScores : [];
            const existingOptionScores = Array.isArray(question?.optionScores) ? question.optionScores : [];
            next.optionScores = (question.options || []).map((_, i) => {
              const raw = suggestionScores[i] ?? existingOptionScores[i] ?? 0;
              const n = Number(raw);
              return Number.isFinite(n) ? Math.max(0, Math.round(n)) : 0;
            });
          }
          return next;
        });

        const importantIds = new Set(batch.pageQuestions.filter((q) => q.important).map((q) => String(q.id)));
        const applyImportance = (s, qId) =>
          importantIds.has(String(qId)) ? Math.round(s * IMPORTANCE_MULTIPLIER) : s;
        const normalized = normalizeScoresToTotal(
          scoped.map((q) => applyImportance(q.maxScore || 0, q.id)),
          batch.budget
        );
        const finalized = scoped.map((q, i) => {
          const nextMaxScore = normalized[i] ?? 0;
          const next = { ...q, maxScore: nextMaxScore };
          if (Array.isArray(next.optionScores)) {
            const clipped = next.optionScores.map((sc) => {
              const n = Number(sc);
              if (!Number.isFinite(n)) return 0;
              return Math.min(Math.max(0, Math.round(n)), nextMaxScore);
            });
            next.optionScores = dedupeOptionScores(clipped, nextMaxScore);
          }
          return next;
        });

        const finalizedById = new Map(finalized.map((q) => [String(q.id), q]));
        if (batch.pageKey === 'default') {
          setDefaultQuestions((prev) => prev.map((q) => finalizedById.get(String(q.id)) || q));
        } else {
          setQuestions((prev) => prev.map((q) => (
            q.pageId === batch.pageKey ? (finalizedById.get(String(q.id)) || q) : q
          )));
        }

        setScoringProgress((prev) => markStepEnd(prev, batch.pageKey, Date.now() - startedAt));
      };

      await Promise.all(batches.map(callBatch));

      showNotification('Punkteverteilung wurde durch KI erstellt. Sie können jetzt alles manuell feinjustieren.');
    } catch (error) {
      console.error('Error setting up scoring with AI:', error);
      showNotification('Fehler bei der KI-Bewertung: ' + error.message, 'error');
    } finally {
      setIsSettingUpScoring(false);
    }
  };

  return (
    <>
    <div className="min-h-screen py-8 relative overflow-clip">
      {/* Template Confirmation Modal */}
      {showTemplateConfirmModal && pendingTemplateData && (
        <TemplateConfirmModal
          templateName={pendingTemplateData.templateName}
          onConfirm={async () => {
            setShowTemplateConfirmModal(false);
            await saveTemplateData(pendingTemplateData.existingTemplate);
            setPendingTemplateData(null);
          }}
          onCancel={() => {
            setShowTemplateConfirmModal(false);
            setPendingTemplateData(null);
          }}
        />
      )}

      {/* Default Questions Template Confirmation Modal */}
      {showDefaultTemplateConfirmModal && pendingDefaultTemplateData && (
        <DefaultTemplateConfirmModal
          templateName={pendingDefaultTemplateData.template.name}
          onConfirm={confirmDeleteDefaultQuestionTemplate}
          onCancel={() => {
            setShowDefaultTemplateConfirmModal(false);
            setPendingDefaultTemplateData(null);
          }}
        />
      )}
      {showMultipleSubmissionWarningModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 p-6 max-w-md w-full">
            <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100 mb-2">
              Mehrfachdurchführung ist aktiviert
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
              Mehrere Bewerber können denselben Fragebogen-Link nutzen. Dieselbe Person darf trotzdem nur einmal einreichen.
            </p>
            <p className="text-sm text-slate-600 dark:text-slate-300 mb-6">
              Ich habe das verstanden und möchte fortfahren.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowMultipleSubmissionWarningModal(false)}
                className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={async () => {
                  setShowMultipleSubmissionWarningModal(false);
                  await generateLinkInternal();
                }}
                className="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors"
              >
                Verstanden, fortfahren
              </button>
            </div>
          </div>
        </div>
      )}

      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 p-6 max-w-2xl w-full">
            <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100 mb-2">
              Fragebogen importieren
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
              Füge die JSON eines KI-generierten Fragebogens ein oder lade eine .json-Datei. Eine zuvor exportierte Datei wird ebenfalls erkannt. Hinweis: Der aktuelle Editor-Inhalt wird ersetzt.
            </p>
            <p className="text-sm text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg p-3 mb-4">
              Hat dieser Fragebogen schon Bewerbungen: Fragen mit gleichem Text behalten ihre ID,
              ihre Antworten bleiben also zugeordnet. Eine Frage, deren Text sich ändert, bekommt
              eine neue — ihre bisherigen Antworten stehen danach in den Ergebnissen unter
              „Nicht mehr im Fragebogen". Verloren geht nichts.
            </p>
            <input
              type="file"
              accept="application/json,.json"
              onChange={handleImportFile}
              className="block w-full text-sm text-slate-600 dark:text-slate-300 mb-3"
            />
            <textarea
              value={importText}
              onChange={(e) => { setImportText(e.target.value); if (importError) setImportError(''); }}
              placeholder={'{ "title": "...", "pages": [ ... ] }'}
              rows={12}
              className="w-full p-3 border border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 rounded-lg text-sm"
              style={{ fontFamily: 'monospace' }}
            />
            {importError && (
              <pre className="mt-3 whitespace-pre-wrap text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg p-3 border border-red-200 dark:border-red-800" style={{ maxHeight: '10rem', overflow: 'auto' }}>{importError}</pre>
            )}
            <div className="flex flex-col sm:flex-row justify-between gap-3 mt-6">
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handleCopyAiPrompt}
                  className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                  title="Vorgefertigten Prompt für einen KI-Chatbot kopieren"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                  KI-Prompt kopieren
                </button>
                <button
                  type="button"
                  onClick={handleDownloadSchema}
                  className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                  title="JSON-Schema des Import-Formats herunterladen"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                  Schema
                </button>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => { setShowImportModal(false); setImportError(''); }}
                  className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  onClick={handleImportConfirm}
                  disabled={!importText.trim()}
                  className="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Importieren
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dynamic Flowing Lines */}
      <FlowingLines theme="emerald" opacity="60" />

      {/* Full width on purpose: the editor is a two-pane workspace, and a
          centred 80rem column wasted the rail's cost without giving the
          question editor room. */}
      <div className="w-full px-4 pb-28 sm:px-6 lg:px-10 lg:pb-10 2xl:px-16 relative z-10">
        <header className="mb-8 text-center">
          <ModernButton
            onClick={() => navigateTo('dashboard')}
            variant="ghost"
            size="sm"
            className="mb-6"
            icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>}
          >
            Zurück zum Dashboard
          </ModernButton>
          <h1 className="text-3xl md:text-5xl font-bold rainbow-headline mb-6 leading-tight pb-2">
            Fragebogen-Editor
          </h1>
          <p className="text-xl text-slate-600 dark:text-slate-300 max-w-2xl mx-auto mt-2">
            Entwirf hier die Fragen für deine Bewerber oder nutze unsere KI-Assistenz.
          </p>
        </header>
        {/* Capped at the page's old width: a single input and two buttons gain
            nothing from stretching across an ultrawide screen. */}
        <div className="max-w-7xl mx-auto">
          <AISection
            aiJobTitle={aiJobTitle}
            setAiJobTitle={setAiJobTitle}
            generateAIQuestions={generateAIQuestions}
            generateAITitle={generateAITitle}
            isLoading={isLoading}
            questions={questions}
            aiShouldFillDescriptions={aiShouldFillDescriptions}
            setAiShouldFillDescriptions={setAiShouldFillDescriptions}
          />
        </div>

        {/* The gap between the columns belongs to the resizer from lg up: it
            brings its own margins, so a second gap would double the distance. */}
        <div ref={layoutRef} style={layoutStyle} className="flex flex-col lg:flex-row gap-8 lg:gap-0 mt-8 items-start">
          {/* Left Sidebar for Navigation (PagesPanel & Scoring) */}
          {/* 20/80 against the full page width by default, adjustable by
              dragging the handle to its right. The width and its floor live in
              `.editor-rail` (input.css) so the stacked mobile layout, which has
              no handle, keeps the full width. */}
          <div ref={railRef} className="rail-scroll editor-rail lg:shrink-0 flex flex-col gap-4 lg:sticky lg:top-[calc(var(--app-nav-height,4rem)+1rem)] lg:max-h-[calc(100vh-var(--app-nav-height,4rem)-2rem)] lg:overflow-y-auto lg:pr-2">
            <div className="bg-white/90 dark:bg-slate-800/80 backdrop-blur-xs rounded-2xl p-5 sm:p-6 border border-slate-200/50 dark:border-slate-700/50 shadow-xl dark:shadow-lg">
              <PagesPanel
                questionPages={questionPages}
                addQuestionPage={addQuestionPage}
                onExport={handleExportQuestionnaire}
                onImport={openImportModal}
                setSelectedPageId={setSelectedPageId}
                selectedPageId={selectedPageId}
                isIntroPageSelected={isIntroPageSelected}
                isDefaultQuestionsPageSelected={isDefaultQuestionsPageSelected}
                orderedEditorQuestionPages={orderedEditorQuestionPages}
                handlePageCardSelect={handlePageCardSelect}
                handlePageCardDragOver={handlePageCardDragOver}
                handlePageCardDragLeave={handlePageCardDragLeave}
                handlePageCardDrop={handlePageCardDrop}
                handlePageCardDragStart={handlePageCardDragStart}
                handlePageCardDragEnd={handlePageCardDragEnd}
                draggedPageCardId={draggedPageCardId}
                dragOverPageCardId={dragOverPageCardId}
                moveDefaultQuestionsPage={moveDefaultQuestionsPage}
                moveQuestionPage={moveQuestionPage}
                removeQuestionPage={removeQuestionPage}
                renameQuestionPage={renameQuestionPage}
                updateQuestionPageDescription={updateQuestionPageDescription}
                updateQuestionPageType={updateQuestionPageType}
                defaultSectionTitle={defaultSectionTitle}
                setDefaultSectionTitle={setDefaultSectionTitle}
                defaultSectionDescription={defaultSectionDescription}
                setDefaultSectionDescription={setDefaultSectionDescription}
                defaultQuestions={defaultQuestions}
                introWelcomeText={introWelcomeText}
                introOverviewText={introOverviewText}
                introAfterSectionsText={introAfterSectionsText}
                introTimerText={introTimerText}
                introStartButtonText={introStartButtonText}
                reviewSectionTitle={reviewSectionTitle}
                totalFormalQuestions={totalFormalQuestions}
                questions={questions}
                onReorderPageQuestions={moveQuestionWithinPage}
                onReorderDefaultQuestions={moveDefaultQuestionWithinList}
                onMovePageQuestion={moveQuestionGroupWithinPage}
                onMoveDefaultQuestion={moveDefaultQuestionGroup}
                onJumpToQuestion={jumpToQuestion}
              />
            </div>

            {/* Punktesystem Section in Sidebar */}
            <div className="bg-white/90 dark:bg-slate-800/80 backdrop-blur-xs rounded-2xl p-4 border border-amber-200/50 dark:border-amber-700/50 shadow-xl dark:shadow-lg">
              <button
                type="button"
                onClick={() => setShowScoringConfig(!showScoringConfig)}
                className="w-full flex items-center justify-between text-left hover:bg-amber-50/50 dark:hover:bg-amber-900/20 rounded-xl transition-colors"
              >
                <div className="flex items-center gap-2">
                  <span className="text-lg">🏆</span>
                  <span className="font-semibold text-slate-700 dark:text-slate-200">Punktesystem</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200">
                    {scoringTotalScore} Pkt
                  </span>
                </div>
                <svg className={`w-5 h-5 text-slate-500 transition-transform duration-200 ${showScoringConfig ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showScoringConfig && (
                <div className="pt-4 mt-4 border-t border-slate-200 dark:border-slate-700 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Gesamtpunktzahl</label>
                    <input
                      type="number"
                      min="1"
                      value={scoringTotalScore}
                      onChange={(e) => setScoringTotalScore(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200"
                    />
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      ~{Math.round(scoringTotalScore / Math.max(1, defaultQuestions.length + questions.length))} Pkt/Frage ({defaultQuestions.length + questions.length})
                    </p>
                  </div>
                  {orderedEditorQuestionPages.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Punkte pro Seite</label>
                      <div className="space-y-2">
                        {orderedEditorQuestionPages.map((entry) => {
                          const pageKey = entry.type === 'default' ? 'default' : entry.id;
                          const pageTitle = entry.type === 'default'
                            ? (defaultSectionTitle || 'Formale Fragen')
                            : (entry.page?.title || `Seite ${entry.questionFlowOrder}`);
                          const currentValue = pageMaxScores[pageKey];
                          const hasValue = currentValue !== undefined && currentValue !== '';
                          return (
                            <div key={pageKey} className="flex items-center gap-2">
                              <span className="text-xs text-slate-600 dark:text-slate-400 flex-1 truncate">{pageTitle}</span>
                              <input
                                type="number"
                                min="0"
                                placeholder=""
                                value={hasValue ? currentValue : ''}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  if (raw === '') {
                                    setPageMaxScores((prev) => {
                                      const next = { ...prev };
                                      delete next[pageKey];
                                      return next;
                                    });
                                  } else {
                                    setPageMaxScores((prev) => ({ ...prev, [pageKey]: Math.max(0, parseInt(raw) || 0) }));
                                  }
                                }}
                                className="w-24 shrink-0 p-1 text-xs border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 text-center"
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Scoring-Erweiterung
                    </label>
                    <textarea
                      value={scoringPrompt}
                      onChange={(e) => setScoringPrompt(e.target.value)}
                      rows={3}
                      className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 text-xs"
                      placeholder="Zusatzregeln..."
                    />
                  </div>
                  <button
                    type="button"
                    onClick={setupScoringWithAI}
                    disabled={isSettingUpScoring}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                  >
                    {isSettingUpScoring ? (
                      <>
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        KI bewertet...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        Punkte einrichten
                      </>
                    )}
                  </button>

                  {isSettingUpScoring && (
                    <EvaluationProgress
                      progress={scoringProgress}
                      title="Punktevergabe läuft (parallel pro Seite)"
                    />
                  )}
                  
                  {(defaultQuestions.length > 0 || questions.length > 0) && (
                    <div className="mt-2 border-t border-slate-200 dark:border-slate-700 pt-3">
                      <h4 className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">Punkteübersicht</h4>
                      {/* No inner scroller: a 12rem box inside the rail, which
                          already scrolls, hid most of the list behind a
                          scrollbar nobody looked for. The rail carries it. */}
                      <div className="space-y-2">
                        {orderedEditorQuestionPages.map((entry) => {
                          const pageKey = entry.type === 'default' ? 'default' : entry.id;
                          const pageTitle = entry.type === 'default'
                            ? (defaultSectionTitle || 'Formale Fragen')
                            : (entry.page?.title || `Seite ${entry.questionFlowOrder}`);
                          const pageQuestions = entry.type === 'default'
                            ? defaultQuestions
                            : questions.filter((q) => q.pageId === entry.id);
                          if (pageQuestions.length === 0) return null;
                          const pageTotal = pageQuestions.reduce((sum, q) => {
                            const ms = Number(q?.maxScore);
                            return sum + (Number.isFinite(ms) && ms >= 0 ? Math.round(ms) : 0);
                          }, 0);
                          return (
                            <div key={pageKey}>
                              <div className="flex items-center justify-between text-[10px] font-semibold text-slate-700 dark:text-slate-300 mb-1">
                                <span className="truncate pr-2">{pageTitle}</span>
                                <span className="font-mono shrink-0">{pageTotal} Pkt</span>
                              </div>
                              {pageQuestions.map((q, i) => {
                                const qText = (q.text || '').replace(/<[^>]*>/g, '').trim() || `Frage ${i + 1}`;
                                const explicitMaxScore = Number(q?.maxScore);
                                const hasExplicitMaxScore = Number.isFinite(explicitMaxScore) && explicitMaxScore >= 0;
                                const optionScores = Array.isArray(q?.optionScores) ? q.optionScores : [];
                                const derivedMaxScore = optionScores.length > 0 ? Math.max(...optionScores.map((score) => Number(score) || 0)) : 0;
                                const maxPts = hasExplicitMaxScore ? Math.round(explicitMaxScore) : Math.round(derivedMaxScore || 0);
                                const hasScores = maxPts > 0 || optionScores.some((score) => Number(score) > 0);
                                return (
                                  <div key={q.id} className="flex items-center justify-between text-[10px] py-0.5 px-1 pl-2 rounded hover:bg-amber-100/50 dark:hover:bg-amber-900/20">
                                    <span className="truncate flex-1 text-slate-600 dark:text-slate-400" title={qText}>{qText}</span>
                                    <span className={`ml-2 font-mono shrink-0 ${hasScores ? 'text-amber-700 dark:text-amber-400' : 'text-slate-400 dark:text-slate-500'}`}>
                                      {hasScores ? `${maxPts} Pkt` : (q.type === 'text' ? 'KI' : '–')}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <RailResizer isResizing={isRailResizing} {...railResizerProps} />

          {/* Right Main Content */}
          {/* Takes whatever the rail leaves. A second fraction here (it was
              3/4 against the rail's 1/5) has to be kept in sync by hand, and
              the leftover showed up as extra space on the right. */}
          <div className="w-full lg:flex-1 lg:min-w-0 flex flex-col gap-8">
            <div className="bg-white/90 dark:bg-slate-800/80 backdrop-blur-xs rounded-2xl p-6 sm:p-8 border border-slate-200/50 dark:border-slate-700/50 shadow-xl dark:shadow-lg">
              <TemplateLoadingSection
                resetEditorToDefaults={resetEditorToDefaults}
                selectedTemplateId={selectedTemplateId}
                setSelectedTemplateId={setSelectedTemplateId}
                loadFromTemplate={loadFromTemplate}
                applyTemplateToEmptyPages={applyTemplateToEmptyPages}
                questionnaireTemplates={questionnaireTemplates}
                deleteTemplateById={deleteTemplateById}
                renameTemplateById={renameTemplateById}
                toggleTemplateArchivedById={toggleTemplateArchivedById}
                templateName={templateName}
                setTemplateName={setTemplateName}
                saveAsTemplate={saveAsTemplate}
                generateLink={generateLink}
                selectedVersionId={selectedVersionId}
                setSelectedVersionId={setSelectedVersionId}
                restoreVersion={restoreVersion}
                versions={versions}
                lastSaveTime={lastSaveTime}
                deleteVersion={deleteVersion}
                saveVersion={saveVersion}
                title={title}
                setTitle={setTitle}
                error={error}
                setError={setError}
                assignmentSection={(
                  <div className="mb-4 max-w-sm">
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Zuständig
                    </label>
                    <div className="relative">
                      <span
                        className="pointer-events-none absolute text-slate-400 dark:text-slate-500"
                        style={{ left: '0.875rem', top: '50%', transform: 'translateY(-50%)' }}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </span>
                      <select
                        value={assignedTo}
                        onChange={(e) => setAssignedTo(e.target.value)}
                        className="w-full p-3 border border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 rounded-lg text-base focus:ring-2 focus:ring-green-500"
                        style={{ paddingLeft: '2.75rem' }}
                      >
                        <option value="">Niemand zugewiesen</option>
                        {accounts.map((account) => (
                          <option key={account.id} value={account.id}>
                            {userDisplayName(account)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                      Verantwortlich für die Verwaltung dieses Fragebogens.
                    </p>
                  </div>
                )}
              />

              {!isIntroPageSelected && !isDefaultQuestionsPageSelected && (
                <EditorActions
                  position="top"
                  onAddQuestion={addQuestion}
                  templateName={templateName}
                  onTemplateNameChange={setTemplateName}
                  onSaveAsTemplate={saveAsTemplate}
                  onGenerateLink={generateLink}
                  allowMultipleSubmissions={allowMultipleSubmissions}
                  onAllowMultipleSubmissionsChange={setAllowMultipleSubmissions}
                />
              )}

              {isIntroPageSelected ? (
                <>
                  <RichTextSections
                    showIntroField={showIntroField}
                    setShowIntroField={setShowIntroField}
                    introTextRef={introTextRef}
                    isTypingIntro={isTypingIntro}
                    debouncedIntroUpdate={debouncedIntroUpdate}
                    introTypingTimeout={introTypingTimeout}
                    setIntroText={setIntroText}
                    applyIntroFormat={applyIntroFormat}
                    showOutroField={showOutroField}
                    setShowOutroField={setShowOutroField}
                    outroTextRef={outroTextRef}
                    isTypingOutro={isTypingOutro}
                    debouncedOutroUpdate={debouncedOutroUpdate}
                    outroTypingTimeout={outroTypingTimeout}
                    setOutroText={setOutroText}
                    applyOutroFormat={applyOutroFormat}
                    showCancelField={showCancelField}
                    setShowCancelField={setShowCancelField}
                    cancelTextRef={cancelTextRef}
                    isTypingCancel={isTypingCancel}
                    debouncedCancelUpdate={debouncedCancelUpdate}
                    cancelTypingTimeout={cancelTypingTimeout}
                    setCancelText={setCancelText}
                    applyCancelFormat={applyCancelFormat}
                    cancelOutroTextRef={cancelOutroTextRef}
                    isTypingCancelOutro={isTypingCancelOutro}
                    debouncedCancelOutroUpdate={debouncedCancelOutroUpdate}
                    cancelOutroTypingTimeout={cancelOutroTypingTimeout}
                    setCancelOutroText={setCancelOutroText}
                    applyCancelOutroFormat={applyCancelOutroFormat}
                  />
                  <IntroPageContent
                    pageNumber={activePageNumber}
                    introOverviewText={introOverviewText}
                    setIntroOverviewText={setIntroOverviewText}
                    introWelcomeText={introWelcomeText}
                    setIntroWelcomeText={setIntroWelcomeText}
                    introAfterSectionsText={introAfterSectionsText}
                    setIntroAfterSectionsText={setIntroAfterSectionsText}
                    reviewSectionTitle={reviewSectionTitle}
                    setReviewSectionTitle={setReviewSectionTitle}
                    reviewSectionDescription={reviewSectionDescription}
                    setReviewSectionDescription={setReviewSectionDescription}
                    introStartButtonText={introStartButtonText}
                    setIntroStartButtonText={setIntroStartButtonText}
                    autoSubmitEnabled={autoSubmitEnabled}
                    setAutoSubmitEnabled={setAutoSubmitEnabled}
                    introTimerText={introTimerText}
                    setIntroTimerText={setIntroTimerText}
                  />
                </>
              ) : isDefaultQuestionsPageSelected ? (
                <DefaultQuestionsContent
                    pageNumber={activePageNumber}
                  defaultSectionTitle={defaultSectionTitle}
                  totalFormalQuestions={totalFormalQuestions}
                  defaultQuestionTemplates={defaultQuestionTemplates}
                  selectedDefaultTemplateId={selectedDefaultTemplateId}
                  setSelectedDefaultTemplateId={setSelectedDefaultTemplateId}
                  loadDefaultQuestionTemplate={loadDefaultQuestionTemplate}
                  deleteDefaultQuestionTemplateById={deleteDefaultQuestionTemplateById}
                  defaultTemplateName={defaultTemplateName}
                  setDefaultTemplateName={setDefaultTemplateName}
                  saveDefaultQuestionTemplate={saveDefaultQuestionTemplate}
                  defaultFormalTemplateId={defaultFormalTemplateId}
                  setFormalTemplateAsDefault={setFormalTemplateAsDefault}
                  resetFormalTemplateDefault={resetFormalTemplateDefault}
                  configuredFormalTemplateName={configuredFormalTemplateName}
                  defaultQuestions={defaultQuestions}
                  handleDefaultDragOver={handleDefaultDragOver}
                  handleDefaultDragLeave={handleDefaultDragLeave}
                  handleDefaultDrop={handleDefaultDrop}
                  defaultDraggedIndex={defaultDraggedIndex}
                  defaultDragOverIndex={defaultDragOverIndex}
                  handleDefaultDragStart={handleDefaultDragStart}
                  handleDefaultDragEnd={handleDefaultDragEnd}
                  setDefaultDraggedIndex={setDefaultDraggedIndex}
                  setDefaultDragOverIndex={setDefaultDragOverIndex}
                  setDefaultQuestionConditional={setDefaultQuestionConditional}
                  updateDefaultQuestion={updateDefaultQuestion}
                  removeDefaultQuestion={removeDefaultQuestion}
                  handleDefaultOptionChange={handleDefaultOptionChange}
                  handleDefaultOptionScoreChange={handleDefaultOptionScoreChange}
                  addDefaultOption={addDefaultOption}
                  removeDefaultOption={removeDefaultOption}
                  handleDefaultOptionDragStart={handleDefaultOptionDragStart}
                  handleDefaultOptionDragOver={handleDefaultOptionDragOver}
                  handleDefaultOptionDragEnd={handleDefaultOptionDragEnd}
                  draggedOptionIndex={draggedOptionIndex}
                  dragOverOptionIndex={dragOverOptionIndex}
                  addDefaultQuestion={addDefaultQuestion}
                />
              ) : (
                <CustomPageContent
                    pageNumber={activePageNumber}
                  activePage={activePage}
                  activePageQuestions={activePageQuestions}
                  activePageType={activePageType}
                  isBasisPageSelected={isBasisPageSelected}
                  isFachPageSelected={isFachPageSelected}
                  selectedBasicTemplateId={selectedBasicTemplateId}
                  setSelectedBasicTemplateId={setSelectedBasicTemplateId}
                  loadBasicQuestionTemplate={loadBasicQuestionTemplate}
                  basicQuestionTemplates={basicQuestionTemplates}
                  defaultBasisTemplateId={defaultBasisTemplateId}
                  deleteBasicQuestionTemplateById={deleteBasicQuestionTemplateById}
                  basicTemplateName={basicTemplateName}
                  setBasicTemplateName={setBasicTemplateName}
                  saveBasicQuestionTemplate={saveBasicQuestionTemplate}
                  setBasisTemplateAsDefault={setBasisTemplateAsDefault}
                  resetBasisTemplateDefault={resetBasisTemplateDefault}
                  configuredBasisTemplateName={configuredBasisTemplateName}
                  selectedFachTemplateId={selectedFachTemplateId}
                  setSelectedFachTemplateId={setSelectedFachTemplateId}
                  loadFachQuestionTemplate={loadFachQuestionTemplate}
                  fachQuestionTemplates={fachQuestionTemplates}
                  defaultFachTemplateId={defaultFachTemplateId}
                  deleteFachQuestionTemplateById={deleteFachQuestionTemplateById}
                  fachTemplateName={fachTemplateName}
                  setFachTemplateName={setFachTemplateName}
                  saveFachQuestionTemplate={saveFachQuestionTemplate}
                  setFachTemplateAsDefault={setFachTemplateAsDefault}
                  resetFachTemplateDefault={resetFachTemplateDefault}
                  configuredFachTemplateName={configuredFachTemplateName}
                  draggedIndex={draggedIndex}
                  dragOverIndex={dragOverIndex}
                  handleDragOver={handleDragOver}
                  handleDragLeave={handleDragLeave}
                  handleDrop={handleDrop}
                  handleDragStart={handleDragStart}
                  handleDragEnd={handleDragEnd}
                  setDraggedIndex={setDraggedIndex}
                  setDragOverIndex={setDragOverIndex}
                  handleOptionDragStart={handleOptionDragStart}
                  handleOptionDragOver={handleOptionDragOver}
                  handleOptionDragEnd={handleOptionDragEnd}
                  draggedOptionIndex={draggedOptionIndex}
                  dragOverOptionIndex={dragOverOptionIndex}
                  setQuestionConditional={setQuestionConditional}
                  updateQuestion={updateQuestion}
                  removeQuestion={removeQuestion}
                  handleOptionChange={handleOptionChange}
                  handleOptionScoreChange={handleOptionScoreChange}
                  addOption={addOption}
                  removeOption={removeOption}
                  questionPages={questionPages}
                  moveQuestionToPage={moveQuestionToPage}
                />
              )}

              <EditorActions
                position="bottom"
                showQuestionButtons={!isIntroPageSelected && !isDefaultQuestionsPageSelected}
                onAddQuestion={addQuestion}
                templateName={templateName}
                onTemplateNameChange={setTemplateName}
                onSaveAsTemplate={saveAsTemplate}
                onGenerateLink={generateLink}
                onExport={handleExportQuestionnaire}
                allowMultipleSubmissions={allowMultipleSubmissions}
                onAllowMultipleSubmissionsChange={setAllowMultipleSubmissions}
              />
            </div>
          </div>
        </div>
      </div>
      {showLinkModal && generatedLink && (
        <LinkModal link={generatedLink} onClose={() => setShowLinkModal(false)} />
      )}
    </div>
    {/* Page Navigation FAB — outside overflow-clip container */}
    <PageNavigationFab
      selectedPageId={selectedPageId}
      setSelectedPageId={setSelectedPageId}
      orderedEditorQuestionPages={orderedEditorQuestionPages}
      isDefaultQuestionsPageSelected={isDefaultQuestionsPageSelected}
      defaultSectionTitle={defaultSectionTitle}
    />
    </>
  );
}
